import { useMemo } from 'react';
import { createFileRoute, Outlet } from '@tanstack/react-router';
import { useIxcProxyvoteV1ProxyJobQueryServiceProxyJobGet } from '@interxfi/api-client/generated/hooks';
import { EntityActionMenu, type EntityActionMenuConfig } from '@interxfi/app-components/components/entity-action-menu/entity-action-menu';
import { PageHeader } from '@interxfi/app-components/components/layout/page-header/page-header';
import {
  DEFAULT_PAGE_LAYOUT_GAP,
  DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE,
  PageLayout,
} from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { AppReadonlyList } from '@interxfi/app-components/components/readonly-list/app-readonly-list';
import { BoxedAppReadonlyList } from '@interxfi/app-components/components/readonly-list/boxed-app-readonly-list';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { VerticalStack } from '@interxfi/design-system/components/alignment/vertical-stack';
import {
  DEFAULT_WRAPPING_LAYOUT_COLUMN,
  LAYOUT_ZERO_CONTAINER_PADDING,
  WrappingLayoutContainer,
} from '@interxfi/design-system/components/layout-container';
import type { ReadonlyListItem } from '@interxfi/design-system/components/list';
import { Typography } from '@interxfi/design-system/components/typography';
import type { IxcProxyvoteV1ProxyJobState } from '@interxfi/types';
import { ProxyVoteJobEventDrawer, ProxyVoteJobEventSummary } from '../../../../../../../../../../../features/proxyvote/job/proxyvote-job-event';
import { getProxyVoteJobStateListItems } from '../../../../../../../../../../../features/proxyvote/job/proxyvote-job-list-items';
import { ProxyVoteJobStatusPill } from '../../../../../../../../../../../features/proxyvote/job/proxyvote-job-status-pill';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../../util/routing/meta';

export const Route = createFileRoute('/_app/_clearing-core/_clearing-required/clearing/vendor/_vendor/beta-nxt/proxy-vote-job/_job/$jobId/_job')({
  head: () => getPageHead('Proxy Vote Job'),
  component: ProxyVoteJobDetail,
});

function getListItems(data: IxcProxyvoteV1ProxyJobState | undefined) {
  const items = getProxyVoteJobStateListItems(data);

  return {
    left: [
      items.data.spec.triggerDate,
      { ...items.data.metrics.total, title: 'Total Tasks' },
      { ...items.data.metrics.pending, title: 'Pending Tasks' },
      { ...items.data.metrics.succeeded, title: 'Succeeded Tasks' },
      { ...items.data.metrics.errored, title: 'Errored Tasks' },
      { ...items.data.metrics.failed, title: 'Failed Tasks' },
    ] as const satisfies ReadonlyListItem[],
    right: [items.metadata.createdAt, items.jobId, items.data.spec.triggerDate] as const satisfies ReadonlyListItem[],
  } as const;
}

function getEntityMenu(jobId: string): EntityActionMenuConfig {
  return {
    eventDrawer: {
      type: 'drawer',
      drawer: {
        Component: ProxyVoteJobEventDrawer,
        props: { request: { jobId } },
      },
    },
    actions: undefined,
  };
}

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load proxy vote job', 'view this proxy vote job');

function ProxyVoteJobDetail() {
  const { jobId } = Route.useParams();
  const request = { jobId } as const;
  const { data, error, isPending } = useIxcProxyvoteV1ProxyJobQueryServiceProxyJobGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);
  const menuItems = useMemo(() => getEntityMenu(jobId), [jobId]);

  const listItems = useMemo(() => getListItems(data?.proxyJob), [data?.proxyJob]);

  return (
    <PageLayout maxWidthContainer={DEFAULT_PAGE_LAYOUT_MAX_WIDTH_CONTAINER_SIZE}>
      <PageLayout.Section noBorder>
        <PageHeader>
          <HorizontalStack gap={2} wrap="nowrap">
            <ProxyVoteJobStatusPill isPending={showPending} size="large" status={data?.proxyJob?.status} />
          </HorizontalStack>

          <EntityActionMenu {...menuItems} />
        </PageHeader>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Events</Typography.Level3>

          <ProxyVoteJobEventSummary gap={DEFAULT_PAGE_LAYOUT_GAP} padding={LAYOUT_ZERO_CONTAINER_PADDING} request={request} />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <VerticalStack gap={4}>
          <Typography.Level3>Data</Typography.Level3>

          <WrappingLayoutContainer
            gap={DEFAULT_PAGE_LAYOUT_GAP}
            left={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <AppReadonlyList isPending={showPending} items={listItems.left} />,
            }}
            padding={LAYOUT_ZERO_CONTAINER_PADDING}
            right={{
              ...DEFAULT_WRAPPING_LAYOUT_COLUMN,
              render: <BoxedAppReadonlyList isPending={showPending} items={listItems.right} />,
            }}
            wrap="forward"
            wrapGap={7}
          />
        </VerticalStack>
      </PageLayout.Section>

      <PageLayout.Section>
        <Outlet />
      </PageLayout.Section>
    </PageLayout>
  );
}
